import {
  Dialog,
  DialogActions,
  Divider,
  Button,
  DialogContent,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { LoadingButton } from '@mui/lab';

import { PERMISSION, Strings } from '@app/constants';
import { useTable } from '@app/components/table';
import { FormProvider, RHFTextField, Scrollbar } from '@app/components';
import { Header } from '@app/sections/header';
import { addRoleSchema } from '@app/modules/role/validator';
import { IAddRoleSchema, IModule, IRole } from '@app/modules/role/types';
import DialogModuleList from './module-list';
import { useRequestState } from '@app/hooks';
import { selectAddRoleState, selectModuleState, selectRoleState } from '@app/modules/role';
import { addRole, getModule } from '@app/modules/role/slice';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  row?: IRole;
}

const AddRoleDialog = ({ open, onClose, row }: Props) => {
  const tableProps = useTable();
  const dispatch = useDispatch();
  const theme = useTheme();

  const methods = useForm<IAddRoleSchema>({
    resolver: yupResolver(addRoleSchema) as any,
    defaultValues: {
      name: row?.name,
      permission: {},
    },
  });

  const onSubmit = (form: IAddRoleSchema) => {
    const id = row?.id ?? 0;
    dispatch(addRole({ ...form, id }));
    console.log(form);
  };

  useEffect(() => {
    dispatch(getModule());
  }, [dispatch]);

  const { data } = useRequestState({
    stateSelector: selectModuleState,
    errorShown: true,
  });
  const { loading } = useRequestState({
    stateSelector: selectAddRoleState,
    onSuccess: onClose,
    successMessageShown: true,
    errorShown: true,
  });
  const disabledModules = [
    'Task Board',
    'Lists',
    'Cards',
    'Checklist',
    'Labels',
    'Dates',
    'Comments',
    'Attachments',
    'Transactions',
    'Tasks',
    'Card Members',
    'Board Members',
    'Board Images',
  ];

  const getNextReservedRole = (roles: IRole[] = []) => {
    const reserved = ['Manager', 'Intern', 'Candidate'];
    for (const role of reserved) {
      if (!roles.some((r) => r.name === role)) return role;
    }
    return null;
  };

  const { data: roleData } = useRequestState({
    stateSelector: selectRoleState,
  });

  const reservedRole = getNextReservedRole(roleData?.records);
  const isReservedRole = reservedRole !== null;

  useEffect(() => {
    /*
     From backend we are receiving permission like
     {
      Employees: ['w', 'r'],
      ModuleName: ['permission']
     }
     to work it with form we have to convert it back to object form so that
     it will work with form
     {
      Employees: { r: true, w: false },
      ModuleName: P'permission': true / false }
     }
    */
    if (row) {
      const reversePermissions: any = {};
      for (const moduleName in row?.permissions) {
        reversePermissions[moduleName] = {};
        Object.values(PERMISSION).forEach((permissionKey) => {
          reversePermissions[moduleName][permissionKey] =
            row.permissions[moduleName].includes(permissionKey);
        });
      }
      methods.setValue('permission', reversePermissions);
      methods.setValue('name', row?.name);
    } else if (isReservedRole) {
      methods.setValue('name', reservedRole);
    }
  }, [methods, row, isReservedRole, reservedRole]);

  const modules: IModule[] =
    data?.records.map((moduleName, index) => ({
      id: index + 1,
      name: moduleName,
      permissions: {},
    })) ?? [];

  return (
    <FormProvider methods={methods}>
      <Dialog open={open}>
        <Header
          title={
            row === undefined
              ? Strings.role.addRoles
              : `${Strings.common.update} ${Strings.role.role}`
          }
          onClose={onClose}
          variant="dialog"
        />
        <Divider />
        <Scrollbar>
          <DialogContent sx={{ p: 0 }}>
            <Stack p={2}>
              <Stack p={2}>
                <RHFTextField
                  name="name"
                  label={Strings.field.roleName}
                  fullWidth
                  variant="outlined"
                  required
                  disabled={isReservedRole}
                />
                {isReservedRole && (
                  <Stack>
                    <Stack sx={{ flexDirection: 'row', gap: 0.6, flexWrap: 'wrap' }}>
                      <Typography
                        fontWeight={theme.typography.fontWeightMedium}
                        sx={{ color: theme.palette.grey[500], fontSize: 11 }}
                      >
                        {Strings.role.note}
                      </Typography>
                      <Typography
                        fontWeight={theme.typography.fontWeightRegular}
                        sx={{ color: theme.palette.grey[500], fontSize: 11 }}
                      >
                        {Strings.role.forEverythingTo}
                      </Typography>
                    </Stack>

                    <Stack
                      sx={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 0.6,
                      }}
                    >
                      <Typography
                        fontWeight={theme.typography.fontWeightMedium}
                        sx={{ color: theme.palette.grey[500], fontSize: 11 }}
                      >
                        {Strings.role.manager}
                      </Typography>
                      <Typography sx={{ color: theme.palette.grey[500], fontSize: 11 }}>
                        →
                      </Typography>

                      <Typography
                        fontWeight={theme.typography.fontWeightMedium}
                        sx={{ color: theme.palette.grey[500], fontSize: 11 }}
                      >
                        {Strings.role.intern}
                      </Typography>
                      <Typography sx={{ color: theme.palette.grey[500], fontSize: 11 }}>
                        →
                      </Typography>

                      <Typography
                        fontWeight={theme.typography.fontWeightMedium}
                        sx={{ color: theme.palette.grey[500], fontSize: 11 }}
                      >
                        {Strings.role.candidate}
                      </Typography>
                      <Typography sx={{ color: theme.palette.grey[500], fontSize: 11 }}>
                        →
                      </Typography>

                      <Typography
                        fontWeight={theme.typography.fontWeightMedium}
                        sx={{ color: theme.palette.grey[500], fontSize: 11 }}
                      >
                        {Strings.role.employee}
                      </Typography>
                      <Typography sx={{ color: theme.palette.grey[500], fontSize: 11 }}>
                        →
                      </Typography>

                      <Typography
                        fontWeight={theme.typography.fontWeightRegular}
                        sx={{ color: theme.palette.grey[500], fontSize: 11 }}
                      >
                        {Strings.role.andSoOn}
                      </Typography>
                    </Stack>

                    <Typography
                      fontWeight={theme.typography.fontWeightRegular}
                      sx={{ color: theme.palette.grey[500], fontSize: 11 }}
                    >
                      {Strings.role.thanksForKeeping}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>
            <DialogModuleList
              loading={false}
              tableProps={tableProps}
              rows={modules}
              disabledModules={disabledModules}
            />
          </DialogContent>
        </Scrollbar>
        <Divider />
        <DialogActions>
          <Button variant="text" onClick={onClose} color="inherit">
            {Strings.button.cancel}
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            loading={loading}
            onClick={methods.handleSubmit(onSubmit)}
          >
            {Strings.button.save}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export { AddRoleDialog };
